import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

const TAB_BASE =
  'inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium ' +
  'whitespace-nowrap transition-all duration-200 ease-smooth';

const TAB_ACTIVE   = 'bg-brand-gradient text-white border border-brand-400/25 shadow-glow-sm';
const TAB_INACTIVE = 'text-gray-400 hover:text-white hover:bg-white/[0.06] border border-transparent';

function TabIcon({ paths }) {
  if (!paths) return null;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
         strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
      {paths}
    </svg>
  );
}

function CountBadge({ value, active }) {
  if (!value) return null;
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
      active ? 'bg-white/20 text-white' : 'bg-brand-500/20 text-brand-200'
    }`}>
      {value}
    </span>
  );
}

/**
 * Több nézetet összefogó fül. A gomb felirata mindig az épp kiválasztott
 * nézetet mutatja, így egy pillantásra látszik, hol állsz — a lenyíló csak a
 * váltáshoz kell.
 */
function TabMenu({ tab, active, onChange }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const activeItem = tab.items.find(i => i.id === active);
  const isActive = !!activeItem;

  // A fülsor tördel, így ez a gomb bárhol lehet a sorban. Ha balra igazítva a
  // menü kilógna a képernyőről, átfordítjuk a jobb szélére — festés előtt,
  // hogy ne legyen villanás.
  useLayoutEffect(() => {
    if (!open || !menuRef.current) { if (!open) setAlignRight(false); return; }
    const rect = menuRef.current.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth - 8) setAlignRight(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${TAB_BASE} ${isActive ? TAB_ACTIVE : TAB_INACTIVE}`}
      >
        <TabIcon paths={tab.icon} />
        {activeItem ? activeItem.label : tab.label}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
             strokeLinecap="round" strokeLinejoin="round"
             className={`w-3 h-3 flex-shrink-0 -mr-0.5 transition-transform duration-200 ${
               open ? 'rotate-180' : ''
             } ${isActive ? 'opacity-70' : 'opacity-50'}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          className={`absolute top-full mt-1.5 z-30 min-w-[13rem] max-w-[calc(100vw-2rem)]
                      p-1 rounded-xl bg-gray-900/95 backdrop-blur-xl border border-white/[0.09]
                      shadow-card-lg animate-scale-in ${alignRight ? 'right-0' : 'left-0'}`}
        >
          {tab.items.map(item => {
            const selected = active === item.id;
            return (
              <button
                key={item.id}
                role="menuitem"
                onClick={() => { onChange(item.id); setOpen(false); }}
                className={`w-full flex items-start gap-2.5 px-3 py-2 rounded-lg text-left
                            transition-colors duration-150 ${
                  selected ? 'bg-white/[0.07] text-white' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <span className="w-4 flex-shrink-0 pt-0.5 text-brand-300">
                  {selected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                         strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{item.label}</span>
                  {item.hint && (
                    <span className="block text-xs text-gray-600 mt-0.5 leading-snug">{item.hint}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A panelek fő fülsora.
 *
 * Szándékosan TÖRDEL és nem görget: több fül a keskenyebb paneleken nem fér ki
 * egy sorba, egy vízszintesen görgethető sáv pedig némán elrejtené az utolsókat.
 *
 * Egy fül `items` tömbbel lenyílóvá válik — így a rokon nézetek (pl. rendelések
 * és előfizetés-előzmény) egy helyet foglalnak a sorban.
 */
export default function PanelTabs({ tabs, active, onChange, className = '' }) {
  return (
    <div
      className={`inline-flex flex-wrap items-center gap-1 p-1 rounded-xl
                  bg-black/25 border border-white/[0.07] ${className}`}
    >
      {tabs.map(t => (
        t.items
          ? <TabMenu key={t.id} tab={t} active={active} onChange={onChange} />
          : (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              className={`${TAB_BASE} ${active === t.id ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              <TabIcon paths={t.icon} />
              {t.label}
              <CountBadge value={t.count} active={active === t.id} />
            </button>
          )
      ))}
    </div>
  );
}
